/**
 * Serviço de Upload de Vídeos para Bunny.net
 * Gerencia todo o fluxo de upload e processamento de vídeos
 */

import { membersBaseURL } from "@/functions/helpers";

export class VideoUploadService {
  constructor(authToken) {
    this.authToken = authToken;
    this.baseURL = membersBaseURL();
  }

  /**
   * Valida arquivo antes do upload
   */
  validateFile(file) {
    const MAX_SIZE = 5 * 1024 * 1024 * 1024; // 5GB
    const ALLOWED_TYPES = [
      "video/mp4",
      "video/quicktime",
      "video/x-msvideo",
      "video/x-ms-wmv",
      "video/webm",
      "video/mpeg"
    ];

    if (file.size > MAX_SIZE) {
      return {
        valid: false,
        error: "Arquivo muito grande. Tamanho máximo: 5GB"
      };
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de arquivo não permitido. Tipos aceitos: ${ALLOWED_TYPES.join(
          ", "
        )}`
      };
    }

    return { valid: true };
  }

  /**
   * Inicializa o upload e retorna URL presigned
   */
  async initUpload({ lessonId, title, file, collectionId }) {
    const validation = this.validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    try {
      const response = await window.axios.post(
        "/bunny-videos/videos/init-upload/",
        {
          lesson_id: lessonId,
          title,
          file_size: file.size,
          file_name: file.name,
          mime_type: file.type,
          collection_id: collectionId
        },
        {
          baseURL: this.baseURL
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Erro ao inicializar upload"
      );
    }
  }

  /**
   * Faz upload direto ao Bunny CDN
   */
  uploadToBunny(uploadUrl, apiKey, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Listener para progresso
      xhr.upload.addEventListener("progress", e => {
        if (e.lengthComputable && onProgress) {
          const percentage = Math.round((e.loaded / e.total) * 100);
          onProgress({
            loaded: e.loaded,
            total: e.total,
            percentage
          });
        }
      });

      // Listener para conclusão
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // Listener para erro
      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      // Listener para abort
      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"));
      });

      // Configurar e enviar
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("AccessKey", apiKey);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  }

  /**
   * Confirma que upload foi concluído
   */
  async completeUpload(videoId) {
    try {
      const response = await window.axios.post(
        `/bunny-videos/videos/${videoId}/complete/`,
        {},
        {
          baseURL: this.baseURL
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Erro ao completar upload"
      );
    }
  }

  /**
   * Verifica status do vídeo
   */
  async getVideoStatus(videoId, noCache = false) {
    try {
      const params = noCache ? { no_cache: 1 } : {};
      const response = await window.axios.get(
        `/bunny-videos/videos/${videoId}/status/`,
        {
          params,
          baseURL: this.baseURL
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Erro ao verificar status"
      );
    }
  }

  /**
   * Cancela upload em andamento
   */
  async cancelUpload(videoId) {
    try {
      const response = await window.axios.post(
        `/bunny-videos/videos/${videoId}/cancel/`,
        {},
        {
          baseURL: this.baseURL
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Erro ao cancelar upload"
      );
    }
  }

  /**
   * Retenta upload que falhou
   */
  async retryUpload(videoId) {
    try {
      const response = await window.axios.post(
        `/bunny-videos/videos/${videoId}/retry/`,
        {},
        {
          baseURL: this.baseURL
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Erro ao retentar upload"
      );
    }
  }

  /**
   * Remove vídeo da aula (desassocia)
   */
  async removeFromLesson(videoId) {
    try {
      const response = await window.axios.post(
        `/bunny-videos/videos/${videoId}/remove-from-lesson/`,
        {},
        {
          baseURL: this.baseURL
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Erro ao remover vídeo da aula"
      );
    }
  }

  /**
   * Aguarda processamento com polling
   */
  async waitForProcessing(
    videoId,
    onStatusChange,
    pollInterval = 3000,
    maxAttempts = 200
  ) {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const status = await this.getVideoStatus(videoId, true);

      if (onStatusChange) {
        onStatusChange(status);
      }

      // Se chegou em um estado final
      if (
        status.status === "ready" ||
        status.status === "failed" ||
        status.status === "cancelled"
      ) {
        return status;
      }

      // Aguardar intervalo
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    }

    throw new Error("Tempo máximo de espera atingido");
  }

  /**
   * Fluxo completo de upload (SEM POLLING)
   * O status será atualizado via webhooks do Bunny.net
   */
  async uploadVideo(params, callbacks = {}) {
    const initData = await this.initUpload(params);

    await this.uploadToBunny(
      initData.upload_url,
      initData.api_key,
      params.file,
      callbacks.onUploadProgress
    );

    const completedData = await this.completeUpload(initData.id);

    // Retorna dados do vídeo (status ainda será "uploading" ou "processing")
    return {
      id: completedData.id || initData.id,
      bunnyVideoId: initData.bunny_video_id,
      title: completedData.title || initData.title || params.title,
      status: completedData.status || "uploading",
      videoUrl: completedData.video_url || initData.video_url,
      embedUrl: completedData.embed_url || initData.embed_url,
      duration: completedData.duration || null
    };
  }
}
